/**
 * A6 — one place to resolve a refused start (V1).
 *
 * The job page's refused-start card offers „Proovi uuesti” and „Loobu ja
 * alusta uuesti”; the shell's OfflineIndicator offered its own retry and
 * „Eemalda seadmest” for the same start on the same screen. The card marks
 * itself here while it is ON SCREEN (`<RefusedStartCardMark>` is rendered
 * inside it), and the indicator leaves that start — and the actions queued
 * behind it — to the card.
 *
 * Marked by presence, not by route: when the job cannot be read (V1-3) the
 * job page draws no card, nothing is marked, and the indicator keeps its own
 * DiscardStartDialog path, as it does on every other screen.
 *
 * Nothing here writes anywhere; it is in-memory screen state only.
 */

import { useEffect, useSyncExternalStore } from 'react';

const marks = new Map<string, number>();
let snapshot: ReadonlySet<string> = new Set();
const listeners = new Set<() => void>();

function publish() {
  snapshot = new Set(marks.keys());
  listeners.forEach((l) => l());
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Render inside the card that shows the refused start `startId`. */
export function RefusedStartCardMark({ startId }: { startId: string }) {
  useEffect(() => {
    marks.set(startId, (marks.get(startId) ?? 0) + 1);
    publish();
    return () => {
      const n = (marks.get(startId) ?? 1) - 1;
      if (n <= 0) marks.delete(startId);
      else marks.set(startId, n);
      publish();
    };
  }, [startId]);
  return null;
}

/** Ids of the refused starts whose card is on screen right now. */
export function useRefusedStartCards(): ReadonlySet<string> {
  return useSyncExternalStore(
    subscribe,
    () => snapshot,
    () => snapshot,
  );
}
